import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { groupsApi } from "@/services/api";
import { useAuthStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Plus, Users, TrendingUp, Wallet, ArrowRight, Loader2 } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl bg-white border border-gray-200 p-5 flex items-center gap-4 hover:shadow-sm transition-shadow"
  >
    <div
      className="h-12 w-12 rounded-xl grid place-items-center"
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      <Icon className="h-6 w-6" />
    </div>
    <div className="min-w-0">
      <div className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">{label}</div>
      <div className="text-xl md:text-2xl font-bold text-[#0D1B2A] truncate">{value}</div>
    </div>
  </div>
);

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await groupsApi.list();
      setGroups(res.data.groups || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalCollected = groups.reduce((s, g) => s + (g.collected || 0), 0);
  const totalPending = groups.reduce((s, g) => s + (g.pending || 0), 0);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  // Personalized time-of-day greeting using the name given at signup
  const hour = new Date().getHours();
  const greetingKey =
    hour < 12 ? "dashboard.greeting_morning" :
    hour < 17 ? "dashboard.greeting_afternoon" :
    "dashboard.greeting_evening";
  const firstName = (user?.name || "").trim().split(/\s+/)[0] || user?.username || "";
  const displayName = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
    : "";

  return (
    <div className="space-y-5 md:space-y-6 animate-fade-up" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0D1B2A] tracking-tight leading-tight"
            data-testid="dashboard-greeting"
          >
            {t(greetingKey, { name: displayName })}
          </h1>
          <p className="text-xs sm:text-sm text-[#4B5563] mt-2">
            {t("dashboard.tagline")}
          </p>
        </div>
        <Button
          data-testid="dashboard-new-group-btn"
          onClick={() => navigate("/groups/new")}
          className="h-11 rounded-xl bg-[#1A3C6E] hover:bg-[#122A4D] text-white font-semibold px-4 sm:px-5 flex items-center gap-2 shrink-0"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden xs:inline sm:inline">{t("dashboard.new_group")}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <StatCard
          icon={Users}
          label={t("dashboard.total_groups")}
          value={groups.length}
          tone={{ bg: "#E8EDF4", fg: "#1A3C6E" }}
          testid="stat-total-groups"
        />
        <StatCard
          icon={Wallet}
          label={t("dashboard.total_collected")}
          value={fmt(totalCollected)}
          tone={{ bg: "#ECFDF5", fg: "#10B981" }}
          testid="stat-collected"
        />
        <StatCard
          icon={TrendingUp}
          label={t("dashboard.total_pending")}
          value={fmt(totalPending)}
          tone={{ bg: "#FEF2F2", fg: "#EF4444" }}
          testid="stat-pending"
        />
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-base md:text-lg font-semibold text-[#0D1B2A]">{t("dashboard.your_groups")}</h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#1A3C6E]" /></div>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center" data-testid="dashboard-empty">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-[#E8EDF4] grid place-items-center mb-4">
            <Users className="h-8 w-8 text-[#1A3C6E]" />
          </div>
          <h3 className="text-lg font-bold text-[#0D1B2A] mb-1">{t("dashboard.empty_title")}</h3>
          <p className="text-sm text-[#4B5563] mb-5 max-w-sm mx-auto">{t("dashboard.empty_desc")}</p>
          <Button
            data-testid="dashboard-create-first-btn"
            onClick={() => navigate("/groups/new")}
            className="h-12 rounded-xl bg-[#1A3C6E] hover:bg-[#122A4D] text-white px-6 font-semibold"
          >
            <Plus className="h-4 w-4 mr-2" /> {t("dashboard.create_first")}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {groups.map((g) => (
            <Link
              key={g.id}
              to={`/groups/${g.id}`}
              data-testid={`group-card-${g.id}`}
              className="group rounded-2xl bg-white border border-gray-200 p-5 hover:border-[#1A3C6E] hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-base font-bold text-[#0D1B2A] truncate">{g.name}</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    {g.member_count}/{g.total_members} {t("dashboard.members")} · ₹{Number(g.monthly_amount).toLocaleString("en-IN")}/mo
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#6B7280] group-hover:text-[#1A3C6E] group-hover:translate-x-1 transition-all" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-[#ECFDF5] px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-[#10B981] font-semibold">
                    {t("dashboard.total_collected")}
                  </div>
                  <div className="text-sm font-bold text-[#065F46]">{fmt(g.collected)}</div>
                </div>
                <div className="rounded-xl bg-[#FEF2F2] px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-[#EF4444] font-semibold">
                    {t("dashboard.total_pending")}
                  </div>
                  <div className="text-sm font-bold text-[#991B1B]">{fmt(g.pending)}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
